import { map, catchError, switchMap, mergeMap, distinctUntilChanged, shareReplay, finalize } from "rxjs/operators";
import { Store, select } from "@ngrx/store";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Injectable } from "@angular/core";
import { of, Subscription, EMPTY, forkJoin, Observable, OperatorFunction, Subject, Subscriber } from "rxjs";
import {
  IAssetPerformanceCollection,
  SecuritySubType,
  IPerformanceByPrivateEquityAsset,
  IPerformanceByEquityAsset,
  IPerformanceByFfxAsset
} from "src/app/models/holdings";
import { ClientEntity } from "src/app/models/clients";
import { IGeneralSelectionState } from "src/app/store/state/general-selection.state";
import { getGeneralSelectionState } from "src/app/store/general-selection/selectors/general-selection.selectors";
import { IHoldingsState } from "src/app/store/state/holdings.state";
import * as HoldingsActions from "src/app/store/holdings/actions/holdings.actions";
import {
  IHoldingsRequest,
  IHoldingsExposuresDataResult,
  IHoldingsValuationsDataResult,
  AssetRevenueState,
  IAssetPerformance
} from "src/app/models/holdings";
import * as ErrorActions from "src/app/store/global-notification/actions/global-notification.action";
import { HoldingsService } from "src/app/services/http/holdings.service";
import { IAppState } from "src/app/store/state/app.state";
import { getHoldingsState } from "src/app/store/holdings/selectors/holdings.selectors";
import {IPortfolio} from "../../../models/portfolios";
import { deduplicatingSwitchMap } from "src/app/utils/deduplicatingSwitchMap";
import {GeneralSelectionFacade} from "../../general-selection/facade/general-selection.facade";

@Injectable()
export class HoldingsEffects {
  holdingsSubscription$: Subscription;
  generalSelectionSubscription$: Subscription;
  selectedAssetPerformanceCollection: IAssetPerformanceCollection;
  selectedAssetRevenue: AssetRevenueState;
  selectedEntities: ClientEntity[] = [];
  selectedPortfolios: IPortfolio[] = [];
  holdingsExposuresData: any;
  errorMessage = "Holdings data couldn't be loaded. Apologies for the inconvenience.";

  constructor(
    private actions$: Actions,
    private holdingsService: HoldingsService,
    private store: Store<IAppState>,
    private generalSelectionFacade: GeneralSelectionFacade
  ) {
    // Feels like gotchas having subscriptions in effects.
    // Better to set them up higher such as in components/facades or avoid using them altogether.
    this.holdingsSubscription$ = this.store
      .pipe(select(getHoldingsState))
      .subscribe((state: IHoldingsState) => {
        this.selectedAssetPerformanceCollection = state !== undefined ? state.selectedAssetPerformanceCollection : {};
        this.selectedAssetRevenue = state !== undefined ? state.selectedAssetRevenue : {};
        this.holdingsExposuresData = state !== undefined ? state.holdingsExposuresData : {};
      });

    this.generalSelectionSubscription$ = this.generalSelectionFacade.getSelectedEntityPortfolioState$
      .subscribe((generalState) => {
        this.selectedEntities = generalState !== undefined ? generalState.selectedEntities : [];
        this.selectedPortfolios = generalState !== undefined ? generalState.selectedPortfolios : [];
      });
  }

  retrieveHoldingsValuationData$ = createEffect(() => this.actions$.pipe(
    ofType(HoldingsActions.GET_HOLDINGS_VALUATION_DATA),
    deduplicatingSwitchMap((action: HoldingsActions.GetHoldingsValuationData) => {
      return this.holdingsService
        .GetHoldingsValuationData(<IHoldingsRequest>action.payload)
        .pipe(
          switchMap((valuation_res: any) => {
            if (valuation_res) {
              const holdingsValuationResultObject: IHoldingsValuationsDataResult = {
                holdingsValuationData: valuation_res,
                date: action.payload.date,
                clientEntities: this.selectedEntities,
                portfolios: this.selectedPortfolios
              };
              return [
                new HoldingsActions.SetHoldingsValuationData(
                  holdingsValuationResultObject
                )
              ];
            }
          }),
          catchError(err => {
            return of(
              new ErrorActions.NotificationErrorAction(
                "Holdings valuation data couldn't be loaded."
              ),
              new HoldingsActions.HoldingsDataError(this.errorMessage)
            );
          })
        );
    })
  ));

  retrieveHoldingsExposureData$ = createEffect(() => this.actions$.pipe(
    ofType(HoldingsActions.GET_HOLDINGS_EXPOSURES_DATA),
    deduplicatingSwitchMap((action: HoldingsActions.GetHoldingsExposuresData) => {
      return this.holdingsService
        .GetHoldingsExposureData(<IHoldingsRequest>action.payload)
        .pipe(
          map((res: any) => {
            if (res) {
              const holdingsResultObject: IHoldingsExposuresDataResult = {
                holdingsExposuresData: res,
                date: action.payload.date,
                clientEntities: this.selectedEntities,
                portfolios: this.selectedPortfolios
              };

              // This kicks off another effect to do another API call.
              // Would be more ideal to have the component or facade dispatch this rather coupling the effects.
              return new HoldingsActions.GetHoldingsAllocationsData(
                holdingsResultObject
              );
            }
          }),
          catchError(err => {
            return of(
              new ErrorActions.NotificationErrorAction(
                "Holdings exposures data couldn't be loaded."
              ),
              new HoldingsActions.HoldingsDataError(this.errorMessage)
            );
          })
        );
    })
  ));

  retrieveHoldingsAllocationsData$ = createEffect(() => this.actions$.pipe(
    ofType(HoldingsActions.GET_HOLDINGS_ALLOCATIONS_DATA),
    deduplicatingSwitchMap((action: HoldingsActions.GetHoldingsAllocationsData) => {
      return this.holdingsService
        .GetHoldingsAllocationsData(<IHoldingsExposuresDataResult>action.payload)
        .pipe(
          switchMap((allocations_res: any) => {
            if (allocations_res) {
              const allocations: {} = {
                ...allocations_res,
                data: {
                  caption: "Asset Allocations",
                  weight: 0,
                  value: 0
                }
              };
              const holdingsResultObject: IHoldingsExposuresDataResult = {
                holdingsExposuresData: action.payload.holdingsExposuresData
                  ? action.payload.holdingsExposuresData.concat(allocations)
                  : [],
                date: action.payload.date,
                clientEntities: this.selectedEntities,
                portfolios: this.selectedPortfolios
              };

              let availableBalance = 0;
              if (allocations_res && allocations_res.AssetAllocations) {
                const cash = allocations_res.AssetAllocations.filter(
                  a =>
                    a.ClassificationKey.includes("Wallet") ||
                    a.ClassificationKey.includes("Cash")
                )[0];

                availableBalance =
                  cash !== undefined && cash.Total ? cash.Total : 0;
              }

              return [
                new HoldingsActions.SetHoldingsExposuresData(
                  holdingsResultObject
                ),
                new HoldingsActions.SetAvailableBalance(availableBalance)
              ];
            }
          }),
          catchError(err => {
            const exposuresDataResult: IHoldingsExposuresDataResult = {
              holdingsExposuresData: this.holdingsExposuresData ? this.holdingsExposuresData : [],
              date: action.payload.date,
              clientEntities: this.selectedEntities,
              portfolios: this.selectedPortfolios
            };
            this.store.dispatch(new HoldingsActions.SetHoldingsExposuresData(exposuresDataResult));

            return of(
              new ErrorActions.NotificationErrorAction("Asset Allocations data couldn't be loaded."),
              new HoldingsActions.HoldingsDataError(this.errorMessage)
            );
          })
        );
    })
  ));

  retrieveAssetPerformance$ = createEffect(() => this.actions$.pipe(
    ofType(HoldingsActions.SELECT_ASSET),
    switchMap((action: HoldingsActions.SelectAsset) => {
      if (!this.selectedAssetPerformanceCollection[action.payload.assetId]) {
        if (action.payload.securitySubType === SecuritySubType.REAL_ESTATE) {
          const assetPerformance: IAssetPerformance = {
            assetId: action.payload.assetId,
            assetName: action.payload.assetName,
            performanceByAsset: action.payload.performanceByAsset,
            positions: null,
            investorActions: null
          };
          return of(new HoldingsActions.SetAssetPerformance(assetPerformance));
        } else {
          return forkJoin([
            this.holdingsService.GetAssetPerformance(action.payload),
            this.holdingsService.GetInvestorActions(action.payload)
            ])
            .pipe(
              map(res => {
                if (res) {
                  let assetPerformance: IAssetPerformance;

                  switch (action.payload.securitySubType) {
                    case SecuritySubType.PRIVATE_EQUITY:
                      assetPerformance = {
                        assetId: action.payload.assetId,
                        assetName: action.payload.assetName,
                        performanceByAsset: res[0] as IPerformanceByPrivateEquityAsset,
                        positions: null,
                        investorActions: res[1]
                      };
                      break;
                    case SecuritySubType.FOREIGNEXCHANGEFORWARD:
                      assetPerformance = {
                        assetId: action.payload.assetId,
                        assetName: action.payload.assetName,
                        performanceByAsset: res[0] as IPerformanceByFfxAsset,
                        positions: null,
                        investorActions: res[1]
                      };
                      break;
                    default:
                      assetPerformance = {
                        assetId: action.payload.assetId,
                        assetName: action.payload.assetName,
                        performanceByAsset: action.payload.performanceByAsset as IPerformanceByEquityAsset,
                        positions: res[0],
                        investorActions: res[1]
                      };
                      break;
                  }
                  return new HoldingsActions.SetAssetPerformance(assetPerformance);
                }
              }),
              catchError(err => {
                return of(
                  new ErrorActions.NotificationErrorAction("Asset Performance couldn't be loaded."),
                  new HoldingsActions.HoldingsDataError(this.errorMessage));
              })
            );
        }
      } else {
        return EMPTY;
      }
    })
  ));
}